/* ============================================================
   PAX Classic — Theme JS  v2
   PlayNation Labs · DotMinds LLC
   ============================================================ */

'use strict';

/* ── Product title locale helper ───────────────────────── */
function getLocalProductTitle(title) {
  var isAr = document.documentElement.lang === 'ar' ||
             document.body.classList.contains('is-rtl');
  if (!isAr) return title;
  var t = title.toLowerCase();
  if (t.indexOf('pax classic') !== -1 || (t.indexOf('pax') !== -1 && t.indexOf('shield') === -1)) {
    return 'باكس كلاسيك';
  }
  if (t.indexOf('pax shield') !== -1 || (t.indexOf('pax') !== -1 && t.indexOf('shield') !== -1)) {
    return 'باكس شيلد';
  }
  return title;
}


const qs  = (sel, ctx = document) => ctx.querySelector(sel);
const qsa = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];

function formatMoney(cents) {
  // cents = integer from Shopify Cart API (already tax-inclusive if store
  // has "Include tax in price" enabled; otherwise we display as-is since
  // the section settings prices are the ones customers see)
  return 'EGP ' + Math.round(cents / 100).toLocaleString('en-EG');
}

function formatEgp(egp) {
  // For display prices that are already whole EGP integers (from section settings)
  return 'EGP ' + Math.round(egp).toLocaleString('en-EG');
}

/* ── Toast ─────────────────────────────────────────────── */
let _toastTimer;
function showToast(msg, icon = '✓') {
  let el = qs('.toast');
  if (!el) {
    el = Object.assign(document.createElement('div'), { className: 'toast' });
    document.body.appendChild(el);
  }
  el.innerHTML = `<span class="toast__icon" aria-hidden="true">${icon}</span><span>${msg}</span>`;
  el.classList.add('show');
  clearTimeout(_toastTimer);
  _toastTimer = setTimeout(() => el.classList.remove('show'), 3200);
}

/* ── Navigation ────────────────────────────────────────── */
function initNav() {
  const nav       = qs('.site-nav');
  const hamburger = qs('.nav-hamburger');
  const mobileNav = qs('.nav-mobile');

  if (nav) {
    window.addEventListener('scroll', () => {
      nav.classList.toggle('scrolled', window.scrollY > 20);
    }, { passive: true });
  }

  if (hamburger && mobileNav) {
    hamburger.addEventListener('click', () => {
      const open = hamburger.classList.toggle('active');
      mobileNav.classList.toggle('open', open);
      hamburger.setAttribute('aria-expanded', String(open));
    });
    qsa('a', mobileNav).forEach(a => {
      a.addEventListener('click', () => {
        hamburger.classList.remove('active');
        mobileNav.classList.remove('open');
        hamburger.setAttribute('aria-expanded', 'false');
      });
    });
  }
}

/* ── Smooth scroll ─────────────────────────────────────── */
function initSmoothScroll() {
  qsa('a[href^="#"]').forEach(a => {
    a.addEventListener('click', e => {
      const id = a.getAttribute('href').slice(1);
      if (!id) return;
      const target = document.getElementById(id);
      if (target) {
        e.preventDefault();
        const top = target.getBoundingClientRect().top + window.scrollY - 64;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    });
  });
}

/* ── Hero parallax ─────────────────────────────────────── */
function initParallax() {
  const img = qs('.hero__product-img');
  if (!img) return;
  window.addEventListener('scroll', () => {
    img.style.transform = `translateY(${window.scrollY * -0.05}px)`;
  }, { passive: true });
}

/* ── Fade-in on scroll ─────────────────────────────────── */
function initFadeIn() {
  if (!window.IntersectionObserver) return;
  const io = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); }
    });
  }, { threshold: 0.1 });
  qsa('.step, .feature-card, .spec-row').forEach(el => {
    el.classList.add('fade-in-target');
    io.observe(el);
  });
}

/* ── Cart Drawer ───────────────────────────────────────── */
const cartDrawer = {
  _drawer: null, _overlay: null,

  init() {
    this._drawer  = qs('.cart-drawer');
    this._overlay = qs('.cart-overlay');
    if (!this._drawer) return;
    this._overlay && this._overlay.addEventListener('click', () => this.close());
    const closeBtn = qs('.cart-drawer__close');
    closeBtn && closeBtn.addEventListener('click', () => this.close());
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this._drawer.classList.contains('open')) this.close();
    });
  },

  open() {
    if (!this._drawer) return;
    this._drawer.classList.add('open');
    this._overlay && this._overlay.classList.add('open');
    document.body.style.overflow = 'hidden';
    this.render();
  },

  close() {
    if (!this._drawer) return;
    this._drawer.classList.remove('open');
    this._overlay && this._overlay.classList.remove('open');
    document.body.style.overflow = '';
  },

  async fetchCart() {
    try {
      const r = await fetch('/cart.js', { credentials: 'same-origin' });
      return r.ok ? r.json() : null;
    } catch { return null; }
  },

  async addItem(variantId, qty) {
    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: Number(variantId), quantity: Number(qty) })
      });
      if (!r.ok) { const err = await r.json().catch(() => ({})); throw new Error(err.description || 'Failed'); }
      return r.json();
    } catch (err) { console.error('addItem:', err); return null; }
  },

  async removeItem(key) {
    try {
      await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: key, quantity: 0 })
      });
      this.render();
    } catch (err) { console.error(err); }
  },

  async render() {
    const body   = qs('.cart-drawer__body');
    const footer = qs('.cart-drawer__footer');
    if (!body) return;
    body.innerHTML = '<div class="cart-drawer__empty" style="opacity:.4">Loading…</div>';

    const cart = await this.fetchCart();
    if (!cart) { body.innerHTML = '<div class="cart-drawer__empty">Could not load cart.</div>'; return; }

    this.updateCount(cart.item_count);

    if (cart.items.length === 0) {
      body.innerHTML = '<div class="cart-drawer__empty">Your cart is empty.</div>';
      if (footer) footer.style.display = 'none';
      return;
    }
    if (footer) footer.style.display = 'block';

    // Read the display prices set in section settings (what customer sees on page)
    // These are whole EGP integers stored in data attributes on the price element
    const priceEl = qs('#js-price-display');
    const saleEgp = priceEl ? parseInt(priceEl.dataset.baseEgp || '0', 10) : 0;
    // Always read from data-original-egp (the single-unit base value),
    // never from textContent which may already be scaled by qty on the page
    const origEgpEl = qs('.price-compare');
    const origEgp   = origEgpEl ? parseInt(origEgpEl.dataset.originalEgp || '0', 10) : 0;

    body.innerHTML = cart.items.map(item => {
      const qty         = item.quantity;
      // Use section-settings prices (tax-inclusive, matching what's shown on page)
      // Fall back to Shopify API prices if settings not available
      const lineSale    = saleEgp > 0 ? saleEgp * qty : Math.round(item.final_line_price / 100);
      const lineOrig    = origEgp > 0 ? origEgp * qty : Math.round((item.original_line_price || item.final_line_price) / 100);
      const hasDiscount = origEgp > 0 && origEgp > saleEgp;

      const priceHTML = hasDiscount
        ? `<span class="cart-item__price-orig">EGP ${lineOrig.toLocaleString('en-EG')}</span>
           <span class="cart-item__price">EGP ${lineSale.toLocaleString('en-EG')}</span>`
        : `<span class="cart-item__price">EGP ${lineSale.toLocaleString('en-EG')}</span>`;

      return `
      <div class="cart-item">
        <div class="cart-item__img">
          <img src="${item.image || ''}" alt="${item.product_title}" width="72" height="72" loading="lazy">
        </div>
        <div class="cart-item__info">
          <div class="cart-item__name">${getLocalProductTitle(item.product_title)}${qty > 1 ? ` <span class="cart-item__qty">×${qty}</span>` : ''}</div>
          <div class="cart-item__price-row">${priceHTML}</div>
          <button class="cart-item__remove" data-key="${item.key}" type="button">Remove</button>
        </div>
      </div>`;
    }).join('');

    qsa('.cart-item__remove', body).forEach(btn => {
      btn.addEventListener('click', () => this.removeItem(btn.dataset.key));
    });

    // Subtotal: use section-settings price × total quantity for tax-inclusive display
    const subtotalEl = qs('.cart-subtotal__amount');
    if (subtotalEl) {
      if (saleEgp > 0) {
        const totalItems = cart.items.reduce((sum, i) => sum + i.quantity, 0);
        subtotalEl.textContent = 'EGP ' + (saleEgp * totalItems).toLocaleString('en-EG');
      } else {
        subtotalEl.textContent = formatMoney(cart.total_price);
      }
    }
  },

  updateCount(n) {
    qsa('.cart-count').forEach(el => {
      el.textContent = n;
      el.style.display = n > 0 ? 'flex' : 'none';
    });
  }
};

/* ── Product Form ──────────────────────────────────────── */
const productForm = {
  qty: 1,
  basePrice: 0,

  init() {
    const form = qs('.product-form');
    if (!form) return;

    // Read base price — data-base-egp is the whole EGP integer (e.g. 1599)
    // data-base-price is cents (e.g. 159900) — kept for cart API calls
    const priceEl = qs('#js-price-display');
    this.basePrice      = priceEl ? parseInt(priceEl.dataset.basePrice || '0', 10) : 0;
    this.basePriceEgp   = priceEl ? parseInt(priceEl.dataset.baseEgp   || '0', 10) : 0;

    // Quantity controls
    const btnMinus   = qs('.quantity-btn[data-action="minus"]');
    const btnPlus    = qs('.quantity-btn[data-action="plus"]');
    const qtyDisplay = qs('#js-qty-display');
    const qtyInput   = qs('#js-qty-input');

    const setQty = (delta) => {
      this.qty = Math.max(1, this.qty + delta);
      if (qtyDisplay) qtyDisplay.textContent = this.qty;
      if (qtyInput)   qtyInput.value = this.qty;
      this._refreshPrice();
    };

    btnMinus && btnMinus.addEventListener('click', () => setQty(-1));
    btnPlus  && btnPlus.addEventListener('click',  () => setQty(+1));

    // Add to cart
    form.addEventListener('submit', async e => {
      e.preventDefault();
      const btn       = qs('.btn-add-to-cart', form);
      const variantId = form.querySelector('[name="id"]')?.value;
      if (!variantId || !btn || btn.disabled) return;

      const orig = btn.innerHTML;
      btn.disabled  = true;
      btn.innerHTML = '<span class="loading-spinner"></span>';

      const result = await cartDrawer.addItem(variantId, this.qty);

      if (result) {
        btn.innerHTML = '&#10003; Added to Cart';
        const productTitle = document.querySelector('.product-title')?.textContent?.trim() || 'Product';
        showToast(productTitle + ' added to cart');
        setTimeout(() => { btn.innerHTML = orig; btn.disabled = false; }, 2400);
        cartDrawer.open();
      } else {
        btn.innerHTML = orig;
        btn.disabled  = false;
        showToast('Could not add to cart. Please try again.', '!');
      }
    });

    // Buy Now
    const buyNowBtn = qs('.btn-buy-now');
    if (buyNowBtn) {
      buyNowBtn.addEventListener('click', async () => {
        const variantId = form.querySelector('[name="id"]')?.value;
        if (!variantId) return;
        const orig = buyNowBtn.textContent;
        buyNowBtn.textContent = 'Processing…';
        buyNowBtn.disabled = true;
        const result = await cartDrawer.addItem(variantId, this.qty);
        if (result) {
          window.location.href = '/checkout';
        } else {
          buyNowBtn.textContent = orig;
          buyNowBtn.disabled = false;
          showToast('Could not proceed. Please try again.', '!');
        }
      });
    }
  },

  _refreshPrice() {
    const priceEl   = qs('#js-price-display');
    const noteEl    = qs('#js-price-note');
    const compareEl = qs('.price-compare');
    const base      = this.basePriceEgp || Math.round(this.basePrice / 100);

    // Update sale price × qty
    if (priceEl && base) {
      priceEl.textContent = (base * this.qty).toLocaleString('en-EG');
    }

    // Update strikethrough original price × qty
    if (compareEl) {
      const origStr  = compareEl.dataset.originalEgp || compareEl.dataset.orig || '0';
      const origBase = parseInt(origStr, 10) || 0;
      if (origBase > 0) {
        compareEl.textContent = 'EGP ' + (origBase * this.qty).toLocaleString('en-EG');
      }
    }

    // Update price note
    if (noteEl) {
      if (this.qty > 1 && base) {
        noteEl.textContent = `EGP ${base.toLocaleString('en-EG')} × ${this.qty} — Price includes VAT`;
      } else {
        noteEl.textContent = 'Price includes VAT';
      }
    }
  }
};

/* ── Boot ──────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  initNav();
  initSmoothScroll();
  initParallax();
  initFadeIn();
  cartDrawer.init();
  productForm.init();
  cartDrawer.fetchCart().then(cart => { if (cart) cartDrawer.updateCount(cart.item_count); });
});
